// missionpick.ts ↔ game/missionlist.cpp (MissionList — the .lvl enumerator over
// the mounted packs) + game/missionlist.h's types + the selectmission.cpp JSON
// getter. The canvas SelectMissionForm class was removed (canvas-removal R1);
// the DOM menu / lobby drive mission selection now (dom/menu-controller.ts +
// dom/lobby-controller.ts), reusing CreateMissionList / MissionList / GetString.
//
// The C++ globals are injected: gpakr → deps.pakr (EnumFiles over the pack
// directory), gppackm → deps.packm (M5: the HTTP DLC pack manager; the default
// seam mounts only the MAIN pack), gppim → deps.ppim (M5: pack info JSON;
// default none), gfDemo → deps.fDemo (the web ships the full version: false).
// The gpcptm complete-manager reaches are compiled OUT of the web build
// (#if !defined(WEB), selectmission.cpp:192-228): no LOCKED/Complete states.

import type { PackFileReader } from '../io/packfile.ts';
import { PACKID_MAIN, type PackId, type MissionIdentifier } from '../io/savegame.ts';
import { LoadIniFile } from '../io/levelpack.ts';

export const kidfSelectMissionWide = 1069;

// game/res.h:143 (the play-form OPTIONS button doubles on this form)
export const kidcSetupGame = 1004;

// game/res.h:294-298, 303
export const kidcCategories = 1500;
export const kidcAddOnMessage = 1501;
export const kidcStoryList = 1502;
export const kidcChallengeList = 1503;
export const kidcAddOnList = 1504;
export const kidcMissionPackInfo = 1002;

// game/missionlist.h:8-9
export const kmtStory = 0;
export const kmtChallenge = 1;
export const kmtAddOn = 2;
export const kmtDemo = 3;
export const kmtMultiplayerChallenge = 4;
export const kmtMultiplayerAddOn = 5;
export const kmtUnknown = 6;
export type MissionType = number;

// game/missionlist.h:24
export const kmltAll = 0;
export const kmltSinglePlayer = 1;
export const kmltMultiplayer = 2;
export type MissionListType = number;

// game/selectmission.cpp:9-10
export const kcUnlockAhead = 1;
export const kfItemLocked = 0x8000;

// game/missionlist.h:11-17
export interface MissionDescription {
  szPackName: string; // char[kcbLevelTitle]
  szPackAuthor: string; // the pack's author (wi/info `a`); empty for the main game
  szLvlTitle: string; // char[kcbLevelTitle]
  cPlayersMin: number;
  cPlayersMax: number;
  mt: MissionType;
}

/** gppackm — the HTTP DLC pack manager (game/httppackmanager.cpp). M5: the
 *  default seam below knows only the always-mounted MAIN pack. */
export interface PackManagerSeam {
  Mount(pakr: PackFileReader, ppackid: PackId): boolean;
  Unmount(pakr: PackFileReader, ppackid: PackId): void;
  EnumPacks(): PackId[];
}

/** gppim — the pack info JSON store (game/httppackinfomanager.cpp). M5. */
export interface PackInfoManagerSeam {
  GetInfoMap(ppackid: PackId): Record<string, unknown> | null;
}

/** The main-pack-only defaults (// M5: the DLC pack store). */
export function mainPackOnlySeams(): { packm: PackManagerSeam; ppim: PackInfoManagerSeam } {
  return {
    packm: {
      Mount: (_pakr, ppackid) => ppackid.id >>> 0 === PACKID_MAIN,
      Unmount: () => {},
      EnumPacks: () => [],
    },
    ppim: { GetInfoMap: () => null },
  };
}

export interface MissionListDeps {
  pakr: PackFileReader; // gpakr
  packm: PackManagerSeam; // gppackm
  ppim: PackInfoManagerSeam; // gppim
  fDemo?: boolean; // gfDemo (web: full version)
}

// the C++ memcmp(&miid.packid, ...) equality (selectmission.cpp:82-83)
export function PackIdsEqual(a: PackId, b: PackId): boolean {
  if (a.id >>> 0 !== b.id >>> 0) return false;
  for (let i = 0; i < 16; i++) {
    if ((a.hash[i] ?? 0) !== (b.hash[i] ?? 0)) return false;
  }
  return true;
}

// game/missionlist.h:41-45
interface LvlItem {
  pszFilename: string;
  mt: MissionType;
  plvliNext: LvlItem | null;
}

// game/missionlist.h:47-53
interface PdbItem {
  packid: PackId;
  pszTitle: string;
  /** the pack's author, from the downloaded pack info JSON (wi/info `a`). Empty for
   *  the main game (its missions are bylined "Spiffcode, Inc." by the menu). */
  pszAuthor: string;
  clvli: number;
  plvliFirst: LvlItem | null;
  ppdbiNext: PdbItem | null;
}

//
// MissionList
//

export class MissionList {
  readonly deps: MissionListDeps; // (the C++ reaches gpakr/gppackm/gppim globally)
  // game/missionlist.cpp:20-22 (ctor)
  private m_ppdbiFirst: PdbItem | null = null;
  private m_mlt: MissionListType = kmltAll;

  constructor(deps: MissionListDeps) {
    this.deps = deps;
  }

  // game/missionlist.h:36-38
  IsMultiplayerMissionType(mt: MissionType): boolean {
    return mt === kmtMultiplayerChallenge || mt === kmtMultiplayerAddOn;
  }

  // game/missionlist.cpp:41-91
  Init(ppackid: PackId | null, mlt: MissionListType): boolean {
    if (this.m_ppdbiFirst !== null) {
      // Assert()
      return true;
    }
    this.m_mlt = mlt;

    if (ppackid !== null) {
      // Add levels from just this pack

      this.AddLevelFiles(ppackid);
    } else {
      // First, enum the main game files

      const packidMain: PackId = { id: PACKID_MAIN, hash: new Uint8Array(16) };
      const ppdbi = this.AddLevelFiles(packidMain);
      if (ppdbi === null) {
        // Assert()
        return false;
      }

      // Now take this off the list for now, because the sorting will be
      // forced later to be at the top

      const ppdbiMain = ppdbi;
      this.m_ppdbiFirst = null;

      // Fix up the main sort single player missions, so S_ goes before C_.

      if (this.m_mlt === kmltAll || this.m_mlt === kmltSinglePlayer) {
        this.ResortMainMissions(ppdbi);
      }

      // Now add addon missions
      for (const packid of this.deps.packm.EnumPacks()) {
        this.AddLevelFiles(packid);
      }

      // Now re-insert main to be first

      ppdbiMain.ppdbiNext = this.m_ppdbiFirst;
      this.m_ppdbiFirst = ppdbiMain;
    }

    return true;
  }

  // game/missionlist.cpp:93-100
  GetCount(): number {
    let clvli = 0;
    for (let ppdbi = this.m_ppdbiFirst; ppdbi !== null; ppdbi = ppdbi.ppdbiNext) {
      clvli += ppdbi.clvli;
    }
    return clvli;
  }

  // game/missionlist.cpp:102-176
  GetMissionDescription(i: number): MissionDescription | null {
    const found = this.FindLevelItem(i);
    if (found === null) {
      return null;
    }
    const { plvli, ppdbi } = found;
    if (!this.deps.packm.Mount(this.deps.pakr, ppdbi.packid)) {
      return null;
    }

    let pszName: string;
    switch (plvli.mt) {
      case kmtStory:
        pszName = 'Story Missions';
        break;

      case kmtMultiplayerChallenge:
      case kmtChallenge:
        pszName = 'Challenge Missions';
        break;

      case kmtDemo:
        pszName = 'Demo Missions';
        break;

      case kmtMultiplayerAddOn:
      case kmtAddOn:
        pszName = ppdbi.pszTitle;
        if (pszName.length === 0) {
          pszName = this.ExtractPackTitle(ppdbi.packid) ?? 'Add-On Mission Pack';
        }
        break;

      default:
        pszName = '<untitled>';
        break;
    }

    const pini = LoadIniFile(this.deps.pakr, plvli.pszFilename);
    if (pini === null) {
      this.deps.packm.Unmount(this.deps.pakr, ppdbi.packid);
      return null;
    }

    const pmd: MissionDescription = {
      szPackName: pszName,
      szPackAuthor: ppdbi.pszAuthor,
      mt: plvli.mt,
      szLvlTitle: pini.GetString('General', 'Title') ?? '<untitled>',
      cPlayersMin: 1,
      cPlayersMax: 1,
    };

    if (this.IsMultiplayerMissionType(plvli.mt)) {
      pmd.cPlayersMin = pini.GetInt('General', 'MinPlayers') ?? 2;
      if (pmd.cPlayersMin < 2) {
        pmd.cPlayersMin = 2;
      }
      pmd.cPlayersMax = pini.GetInt('General', 'MaxPlayers') ?? 2;
      if (pmd.cPlayersMax > 4) {
        pmd.cPlayersMax = 4;
      }
    } else {
      pmd.cPlayersMin = 1;
      pmd.cPlayersMax = 1;
    }

    this.deps.packm.Unmount(this.deps.pakr, ppdbi.packid);
    return pmd;
  }

  // game/missionlist.cpp:178-208
  ExtractPackTitle(ppackid: PackId): string | null {
    const map = this.deps.ppim.GetInfoMap(ppackid);
    if (map === null) {
      return null;
    }

    const obj = map['t'];
    if (typeof obj !== 'string') {
      return null;
    }

    let fWhitespace = true;
    for (const ch of obj) {
      if (!/\s/.test(ch)) {
        fWhitespace = false;
        break;
      }
    }
    if (fWhitespace) {
      return null;
    }

    return obj;
  }

  // game/missionlist.cpp:210-220
  GetMissionIdentifier(i: number): MissionIdentifier | null {
    const found = this.FindLevelItem(i);
    if (found === null) {
      return null;
    }
    return {
      packid: { id: found.ppdbi.packid.id, hash: found.ppdbi.packid.hash.slice() },
      szLvlFilename: found.plvli.pszFilename,
    };
  }

  // game/missionlist.cpp:222-247
  private FindLevelItem(i: number): { plvli: LvlItem; ppdbi: PdbItem } | null {
    if (i < 0) {
      return null;
    }
    let fFound = false;
    let ppdbi = this.m_ppdbiFirst;
    for (; ppdbi !== null; ppdbi = ppdbi.ppdbiNext) {
      if (i < ppdbi.clvli) {
        fFound = true;
        break;
      }
      i = i - ppdbi.clvli;
    }
    if (!fFound || ppdbi === null) {
      return null;
    }
    for (let plvli = ppdbi.plvliFirst; plvli !== null; plvli = plvli.plvliNext) {
      if (i === 0) {
        return { plvli, ppdbi };
      }
      i--;
    }
    return null;
  }

  // game/missionlist.cpp:249-291
  private AddLevelFiles(ppackid: PackId): PdbItem | null {
    if (!this.deps.packm.Mount(this.deps.pakr, ppackid)) {
      return null;
    }

    let ppdbi: PdbItem | null = null;
    // (ppackid->id == PACKID_MAIN) ? PACKENUM_FIRST : PACKENUM_LAST
    const key = ppackid.id >>> 0 === PACKID_MAIN ? 'first' : 'last';
    for (const szFn of this.deps.pakr.EnumFiles(key)) {
      const cch = szFn.length;
      if (cch < 4) {
        continue;
      }
      if (szFn.slice(cch - 4) !== '.lvl') {
        continue;
      }
      let mlt: MissionListType;
      if (this.IsMultiplayerMissionType(this.GetMissionType(ppackid, szFn))) {
        mlt = kmltMultiplayer;
      } else {
        mlt = kmltSinglePlayer;
      }
      if (mlt !== this.m_mlt && this.m_mlt !== kmltAll) {
        continue;
      }
      if (!this.PassesDemoFilter(szFn, ppackid.id >>> 0 !== PACKID_MAIN)) {
        continue;
      }

      // Alloc PdbItem only if there are the desired missions
      if (ppdbi === null) {
        ppdbi = this.AddPdbItem(ppackid);
        if (ppdbi === null) {
          continue;
        }
      }
      this.AddLvlItem(ppdbi, szFn);
    }
    this.deps.packm.Unmount(this.deps.pakr, ppackid);

    return ppdbi;
  }

  // game/missionlist.cpp:293-337
  private AddPdbItem(ppackid: PackId): PdbItem | null {
    const ppdbi: PdbItem = {
      packid: { id: ppackid.id, hash: ppackid.hash.slice() },
      plvliFirst: null,
      ppdbiNext: null,
      clvli: 0,
      pszTitle: '',
      pszAuthor: '',
    };

    if (ppackid.id >>> 0 === PACKID_MAIN) {
      ppdbi.pszTitle = 'Main Game';
    } else {
      const map = this.deps.ppim.GetInfoMap(ppackid);
      if (map !== null) {
        const obj = map['t'];
        if (typeof obj === 'string') {
          ppdbi.pszTitle = obj;
        }
        // the pack's author (wi/info `a`) — shown as the Add-On mission byline.
        const author = map['a'];
        if (typeof author === 'string') {
          ppdbi.pszAuthor = author;
        }
      }
      // (the NULL-title arm becomes the '' initializer)
    }

    let fInserted = false;
    // PdbItem **pppdbiT = &m_ppdbiFirst — the insertion-point walk
    let prev: PdbItem | null = null;
    let cur = this.m_ppdbiFirst;
    while (cur !== null) {
      if (ppdbi.pszTitle < cur.pszTitle) {
        ppdbi.ppdbiNext = cur;
        if (prev === null) this.m_ppdbiFirst = ppdbi;
        else prev.ppdbiNext = ppdbi;
        fInserted = true;
        break;
      }
      prev = cur;
      cur = cur.ppdbiNext;
    }
    if (!fInserted) {
      if (prev === null) this.m_ppdbiFirst = ppdbi;
      else prev.ppdbiNext = ppdbi;
    }
    return ppdbi;
  }

  // game/missionlist.cpp:339-369
  private AddLvlItem(ppdbi: PdbItem, pszLvl: string): LvlItem | null {
    const plvli: LvlItem = {
      pszFilename: pszLvl,
      mt: this.GetMissionType(ppdbi.packid, pszLvl),
      plvliNext: null,
    };

    let fInserted = false;
    let prev: LvlItem | null = null;
    let cur = ppdbi.plvliFirst;
    while (cur !== null) {
      if (plvli.pszFilename < cur.pszFilename) {
        plvli.plvliNext = cur;
        if (prev === null) ppdbi.plvliFirst = plvli;
        else prev.plvliNext = plvli;
        fInserted = true;
        break;
      }
      prev = cur;
      cur = cur.plvliNext;
    }
    if (!fInserted) {
      if (prev === null) ppdbi.plvliFirst = plvli;
      else prev.plvliNext = plvli;
    }
    ppdbi.clvli++;
    return plvli;
  }

  // game/missionlist.cpp:371-389
  private ResortMainMissions(ppdbi: PdbItem): void {
    // Move the c_ missions after the s_ missions.
    // Find the end of the list

    let last: LvlItem | null = ppdbi.plvliFirst;
    while (last !== null && last.plvliNext !== null) {
      last = last.plvliNext;
    }

    // Move all non-s_'s to the end. Keep relative order.

    while (ppdbi.plvliFirst !== null && !ppdbi.plvliFirst.pszFilename.startsWith('s_')) {
      const plvliT = ppdbi.plvliFirst;
      ppdbi.plvliFirst = plvliT.plvliNext;
      plvliT.plvliNext = null;
      if (last === null) {
        ppdbi.plvliFirst = plvliT;
      } else {
        last.plvliNext = plvliT;
      }
      last = plvliT;
    }
  }

  // game/missionlist.cpp:391-407
  private PassesDemoFilter(psz: string, fAddOn: boolean): boolean {
    // Everything is available if not demo
    if (!(this.deps.fDemo ?? false)) {
      return true;
    }
    // Addon's not available if demo
    if (fAddOn) {
      return false;
    }
    // Only m_12 available as multiplayer
    if (this.m_mlt === kmltAll || this.m_mlt === kmltMultiplayer) {
      return psz === 'm_12.lvl';
    }
    // Only s_00-03 available single player
    return psz === 's_00.lvl' || psz === 's_01.lvl' || psz === 's_02.lvl' || psz === 's_03.lvl';
  }

  // game/missionlist.cpp:409-438
  GetMissionType(ppackid: PackId, pszLvl: string): MissionType {
    if (ppackid.id >>> 0 === PACKID_MAIN) {
      if (pszLvl[1] === '_') {
        switch (pszLvl[0]) {
          case 's':
            return kmtStory;

          case 'c':
            return kmtChallenge;

          case 'd':
            // HACK: D3 is the only demo mission in the database
            // let's considder it a challenge map
            return kmtChallenge; // return kmtDemo;

          case 'm':
            return kmtMultiplayerChallenge;
        }
      }
      return kmtUnknown;
    }

    if (pszLvl[0] === 'm' && pszLvl[1] === '_') {
      return kmtMultiplayerAddOn;
    }

    return kmtAddOn;
  }
}

// game/missionlist.cpp:8-18
export function CreateMissionList(
  deps: MissionListDeps,
  ppackid: PackId | null,
  mlt: MissionListType,
): MissionList | null {
  const pml = new MissionList(deps);
  if (!pml.Init(ppackid, mlt)) {
    return null;
  }
  return pml;
}

// game/selectmission.cpp:15-35 — JSON-map string getter (whitespace = missing)
export function GetString(map: Record<string, unknown>, key: string): string | null {
  const obj = map[key];
  if (typeof obj !== 'string') {
    return null;
  }

  let fWhitespace = true;
  for (const ch of obj) {
    if (!/\s/.test(ch)) {
      fWhitespace = false;
      break;
    }
  }
  if (fWhitespace) {
    return null;
  }
  return obj;
}
